import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "primereact/dialog";
import { addAibox, aiboxLogin } from "../../../../../../Api Methods/Api";
import classes from "../../RuleWizard/RuleWizard.module.css";
import AiboxStepUsage from "./AiboxStepUsage";
import AiboxStepOutput from "./AiboxStepOutput";
import { AiboxStepLoggingIn, AiboxStepLogin } from "./AiboxStepLogin";
import AiboxStepControlType, { buildZoneConfig, getControlLevelLabel } from "./AiboxStepControlType";
import AiboxStepLiveView from "./AiboxStepLiveView";
import AiboxStepResult from "./AiboxStepResult";

// Ganz AiBox setup wizard - laid out like the RuleWizard (step sidebar + content pane).
// Flow: Usage -> [Output] -> Login -> Logging in -> [Control type] -> [Live view channels] -> Result
// Bracketed steps only appear when "Control" / "Live view" was picked on the Usage step.

const AiboxForm = ({ setShowVirtualSource, outputs, fetchVirtualSources }) => {
    const dispatch = useDispatch();
    const client = useSelector(state => state.auth.client);
    const token = useSelector(state => state.auth.token);
    const DeviceUUID = useSelector(state => state.auth.DeviceUUID);
    const userID = useSelector(state => state.auth.userID);
    const apiData = { token, client };

    const [currentStep, setCurrentStep] = useState("usage");
    const [completedSteps, setCompletedSteps] = useState(new Set());

    const [selectedOptions, setSelectedOptions] = useState([]);
    const [pinSelected, setPinSelected] = useState(null);
    const [credentials, setCredentials] = useState({ hostName: "", port: "", username: "", password: "" });
    const [loginError, setLoginError] = useState("");

    const [channels, setChannels] = useState([]);
    const [rules, setRules] = useState([]);
    const [relays, setRelays] = useState([]);
    const [integrationID, setIntegrationID] = useState(0);

    const [controlLevel, setControlLevel] = useState(null);
    const [selectedControlKeys, setSelectedControlKeys] = useState([]);
    const [zoneName, setZoneName] = useState("");
    const [selectedLiveViewChannels, setSelectedLiveViewChannels] = useState([]);

    const [linkStatus, setLinkStatus] = useState("linking");
    const [failReason, setFailReason] = useState("");

    const wantsControl = selectedOptions.includes("Control");
    const wantsLiveView = selectedOptions.includes("Live view");

    const stepConfigs = [
        { step: "usage", label: "Usage" },
        wantsControl && { step: "output", label: "Select output" },
        { step: "login", label: "Login" },
        { step: "loggingIn", label: "Logging in" },
        wantsControl && { step: "controlType", label: "Control type" },
        wantsLiveView && { step: "liveView", label: "Live view channels" },
        { step: "result", label: "Finish" },
    ].filter(Boolean);

    const stepOrder = stepConfigs.map(c => c.step);
    // Last step the user configures before linking - its Next button becomes "Link AiBox"
    const lastSelectionStep = stepOrder[stepOrder.length - 2];

    const goTo = (step) => {
        setCompletedSteps(prev => new Set(prev).add(currentStep));
        setCurrentStep(step);
    };

    const nextStep = (from) => stepOrder[stepOrder.indexOf(from) + 1];
    // "Logging in" is transient, so going back skips over it to the Login form
    const prevStep = (from) => {
        const prev = stepOrder[stepOrder.indexOf(from) - 1];
        return prev === "loggingIn" ? "login" : prev;
    };

    const HandleClose = () => setShowVirtualSource(null);

    const HandleLogin = async () => {
        setLoginError("");
        goTo("loggingIn");
        try {
            const loginResponse = await aiboxLogin(apiData, { protocol: "https", ...credentials, userID }, dispatch);
            if (loginResponse?.success) {
                setChannels(loginResponse.data?.channels ?? []);
                setRules(loginResponse.data?.systemRules ?? []);
                setRelays(loginResponse.data?.relayOutputs ?? []);
                setIntegrationID(loginResponse.integrationId);
                setCompletedSteps(prev => new Set(prev).add("login").add("loggingIn"));
                setCurrentStep(nextStep("loggingIn"));
            } else {
                setLoginError(loginResponse?.message || "Failed to login to AiBox, please check your credentials & try again");
                setCurrentStep("login");
            }
        } catch {
            setLoginError("Failed to login to AiBox, please check your credentials & try again");
            setCurrentStep("login");
        }
    };

    const HandleLink = async () => {
        goTo("result");
        setLinkStatus("linking");

        const body = {
            siteUUID: DeviceUUID,
            pinNumber: wantsControl ? pinSelected : -1,
            userID,
            ipAndPort: `${credentials.hostName}:${credentials.port}`,
            zoneConfig: wantsControl ? buildZoneConfig({ controlLevel, selectedControlKeys, zoneName }) : [],
            integrationId: integrationID,
            liveViewChannels: wantsLiveView ? selectedLiveViewChannels : [],
        };

        console.log(body);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15_000);

        try {
            const linkResponse = await addAibox(apiData, body, dispatch, controller.signal);
            if (linkResponse?.success) {
                fetchVirtualSources();
                setLinkStatus("success");
            } else {
                setFailReason(linkResponse?.message || "Failed to link AiBox, please try again.");
                setLinkStatus("failed");
            }
        } catch (err) {
            setFailReason(err.name === "AbortError"
                ? "The request timed out. Please check your details and try again."
                : "An unexpected error occurred. Please try again.");
            setLinkStatus("failed");
        } finally {
            clearTimeout(timeoutId);
        }
    };

    const advanceFrom = (step) => step === lastSelectionStep ? HandleLink() : goTo(nextStep(step));

    const getStepSummary = (step) => {
        switch (step) {
            case "usage":
                return selectedOptions.join(" + ") || null;
            case "output":
                return outputs.find(o => o.Pin === pinSelected)?.pinName ?? null;
            case "login":
                return credentials.hostName || null;
            case "loggingIn":
                return "Connected";
            case "controlType":
                if (!controlLevel) return null;
                return controlLevel === "hub"
                    ? "Hub"
                    : `${getControlLevelLabel(controlLevel)} (${selectedControlKeys.length})`;
            case "liveView":
                return `${selectedLiveViewChannels.length} channel${selectedLiveViewChannels.length === 1 ? "" : "s"}`;
            default:
                return null;
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case "usage":
                return (
                    <AiboxStepUsage
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        onNext={() => goTo(nextStep("usage"))}
                    />
                );
            case "output":
                return (
                    <AiboxStepOutput
                        outputs={outputs}
                        pinSelected={pinSelected}
                        setPinSelected={setPinSelected}
                        onNext={() => goTo(nextStep("output"))}
                        onBack={() => setCurrentStep(prevStep("output"))}
                    />
                );
            case "login":
                return (
                    <AiboxStepLogin
                        credentials={credentials}
                        setCredentials={setCredentials}
                        loginError={loginError}
                        onLogin={HandleLogin}
                        onBack={() => setCurrentStep(prevStep("login"))}
                    />
                );
            case "loggingIn":
                return <AiboxStepLoggingIn onClose={HandleClose} />;
            case "controlType":
                return (
                    <AiboxStepControlType
                        channels={channels}
                        rules={rules}
                        relays={relays}
                        controlLevel={controlLevel}
                        setControlLevel={setControlLevel}
                        selectedControlKeys={selectedControlKeys}
                        setSelectedControlKeys={setSelectedControlKeys}
                        zoneName={zoneName}
                        setZoneName={setZoneName}
                        isLastStep={lastSelectionStep === "controlType"}
                        onNext={() => advanceFrom("controlType")}
                        onBack={() => setCurrentStep(prevStep("controlType"))}
                    />
                );
            case "liveView":
                return (
                    <AiboxStepLiveView
                        channels={channels}
                        selectedLiveViewChannels={selectedLiveViewChannels}
                        setSelectedLiveViewChannels={setSelectedLiveViewChannels}
                        isLastStep={lastSelectionStep === "liveView"}
                        onNext={() => advanceFrom("liveView")}
                        onBack={() => setCurrentStep(prevStep("liveView"))}
                    />
                );
            case "result":
                return (
                    <AiboxStepResult
                        status={linkStatus}
                        failReason={failReason}
                        onBack={() => setCurrentStep(lastSelectionStep)}
                        onClose={HandleClose}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Dialog
            visible
            onHide={HandleClose}
            style={{ width: "900px" }}
            header="Link Ganz AiBox"
            modal
            pt={{ content: { style: { padding: 0 } } }}
        >
            <div className={classes.wizardContainer}>
                <div className={classes.sidebar}>
                    <span className={classes.sidebarTitle}>AiBox Setup</span>
                    {stepConfigs.map((config, i) => {
                        const isActive = config.step === currentStep;
                        const isDone = completedSteps.has(config.step) && !isActive;
                        const summary = isDone ? getStepSummary(config.step) : null;
                        return (
                            <div
                                key={config.step}
                                className={`${classes.stepItem} ${isActive ? classes.stepItemActive : ""}`}
                            >
                                <div
                                    className={`${classes.stepNumber} ${
                                        isActive ? classes.stepNumberActive : isDone ? classes.stepNumberDone : ""
                                    }`}
                                >
                                    {isDone ? "✓" : i + 1}
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                    <span
                                        className={`${classes.stepLabel} ${
                                            isActive ? classes.stepLabelActive : isDone ? classes.stepLabelDone : ""
                                        }`}
                                    >
                                        {config.label}
                                    </span>
                                    {summary && <span className={classes.stepSummary}>{summary}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className={classes.content}>
                    {renderStep()}
                </div>
            </div>
        </Dialog>
    );
};

export default AiboxForm;
